import fs from "node:fs/promises";
import path from "node:path";
import { COMPLETION_STATUS_TEXT } from "../../core/statuses.js";

/**
 * Claude Adapter - Transforms OpenCode agents and skills into CLAUDE.md and .claude/rules/.
 */
export class ClaudeAdapter {
  cwd: string;

  constructor({ cwd }: { cwd: string }) {
    this.cwd = cwd;
  }

  /**
   * Transforms an OpenCode agent or skill into a Claude rule with completion contract.
   */
  async transformToRule(filePath: string, type: "agent" | "skill" = "agent"): Promise<{ content: string; name: string }> {
    const content = await fs.readFile(filePath, "utf8");
    const fileName = path.basename(filePath, ".md") === "SKILL" 
      ? path.basename(path.dirname(filePath)) 
      : path.basename(filePath, ".md");
    
    const ruleName = `${type}-${fileName.toLowerCase()}`;
    
    const hardenedContent = `---
name: ${ruleName}
description: ${type === "agent" ? "Persona" : "Skill"} rule for ${fileName}
---
# ${fileName} ${type === "agent" ? "Persona" : "Skill"}

${content}

##  Completion Contract (Mandatory)
Every task completion MUST provide the following payload:
1. **Status**: ${COMPLETION_STATUS_TEXT}
2. **Scope reviewed**: Brief summary of what was analyzed.
3. **Findings**: Evidence-based discoveries.
4. **Files affected**: List of all concrete paths modified or created.
5. **Validation/evidence**: Commands run and their results.
6. **Risks/notes**: Any caveats or technical debt introduced.
7. **Required manager action**: Clear next step for the orchestrator.
8. **Can manager continue**: [yes/no]
`;

    return {
      content: hardenedContent,
      name: ruleName
    };
  }

  /**
   * Deploys agents, skills, and root CLAUDE.md.
   */
  async deploy(installRoot: string = ".ai-workflow"): Promise<void> {
    const rulesDir = path.join(this.cwd, ".claude/rules");
    await fs.mkdir(rulesDir, { recursive: true });

    const sourceAgentsDir = path.join(this.cwd, installRoot, "opencode/agents");
    const sourceSkillsDir = path.join(this.cwd, installRoot, "opencode/skills");
    const sourceCommandsDir = path.join(this.cwd, installRoot, "opencode/commands");

    // 1. Deploy Agents to .claude/rules/agent-*.md
    const agentFiles = await fs.readdir(sourceAgentsDir).catch(() => []);
    for (const file of agentFiles) {
      if (!file.endsWith(".md")) continue;
      const transformed = await this.transformToRule(path.join(sourceAgentsDir, file), "agent");
      await fs.writeFile(path.join(rulesDir, `${transformed.name}.md`), transformed.content);
    }

    // 2. Deploy Skills to .claude/rules/skill-*.md
    const skillFolders = await fs.readdir(sourceSkillsDir).catch(() => []);
    for (const folder of skillFolders) {
      const skillSource = path.join(sourceSkillsDir, folder, "SKILL.md");
      if (await this.exists(skillSource)) {
        const transformed = await this.transformToRule(skillSource, "skill");
        await fs.writeFile(path.join(rulesDir, `${transformed.name}.md`), transformed.content);
      }
    }

    // 3. Create root CLAUDE.md
    await this.deployRootClaudeMd(sourceCommandsDir);
  }

  async deployRootClaudeMd(sourceCommandsDir: string): Promise<void> {
    const targetPath = path.join(this.cwd, "CLAUDE.md");
    
    // Collect commands to document them in CLAUDE.md
    const commandFiles = await fs.readdir(sourceCommandsDir).catch(() => []);
    let commandsDoc = "";
    for (const file of commandFiles) {
      if (!file.endsWith(".md")) continue;
      const name = path.basename(file, ".md");
      commandsDoc += `- \`ai-workflow run --spec-path=...\` (Target: ${name})\n`;
    }

    const instructions = `# CLAUDE.md - AI Workflow Kit Governance

This project uses the **AI Workflow Kit** (OpenCode-first) workflow.

##  Mandate: Atlas Authority Protocol
All architectural changes must align with the specifications issued by the **Specification Authority** (./specs).

##  Common Commands
${commandsDoc}
- \`ai-workflow doctor\`: Verify installation.
- \`ai-workflow collect-evidence\`: Generate EVIDENCE.json.

##  Rules & Personas
Adhere to the specific rules and personas defined in \`.claude/rules/\`. 
- Always follow the **Branch Gate** policy (never work on \`main\`).
- Always follow the **SDD Workflow** (Spec -> Plan -> PR -> Evidence).

##  Completion Contract
Every task completion MUST follow the payload format defined in the individual rule files.
`;
    await fs.writeFile(targetPath, instructions);
  }

  async exists(p: string): Promise<boolean> {
    try {
      await fs.access(p);
      return true;
    } catch {
      return false;
    }
  }
}
